import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Gauge, Loader2, PauseCircle, RefreshCw, WifiOff } from 'lucide-react';
import { useMyPilotRatedMatches, useMyPilotRating, useMyPilotRatingHistory, useRatingPilotContexts } from '@tark/shared/hooks';
import {
  ratingPilotEvidenceLabel,
  ratingPilotHeadline,
  ratingPilotIntervalLabel,
  ratingPilotSurfaceState,
  ratingPilotSnapshotLabel,
  ratingPilotThroughLabel,
} from '@tark/shared/lib';
import type { RatingPilotSurfaceState } from '@tark/shared/lib';
import { RatingPilotHero } from '../components/matches/RatingPilotHero';
import { RatingPilotScaleBand } from '../components/matches/RatingPilotScaleBand';
import { RatingPilotJourney } from '../components/matches/RatingPilotJourney';
import { RatingPilotRatedMatches } from '../components/matches/RatingPilotRatedMatches';

const PILOT_UI_ENABLED = import.meta.env.VITE_RATING_PILOT_UI_ENABLED === 'true';

export default function PilotRatingPage() {
  const navigate = useNavigate();
  const contextsQuery = useRatingPilotContexts(PILOT_UI_ENABLED);
  const [selectedSquadId, setSelectedSquadId] = useState<number | null>(null);
  const selectedContext = contextsQuery.data?.find(context => context.squadId === selectedSquadId)
    ?? contextsQuery.data?.[0];
  const ratingQuery = useMyPilotRating(
    PILOT_UI_ENABLED && Boolean(selectedContext),
    selectedContext?.squadId,
  );
  const historyQuery = useMyPilotRatingHistory(
    PILOT_UI_ENABLED && Boolean(selectedContext),
    selectedContext?.squadId,
  );
  const ratedMatchesQuery = useMyPilotRatedMatches(
    PILOT_UI_ENABLED && Boolean(selectedContext),
    selectedContext?.squadId,
  );
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
  const rating = ratingQuery.data;
  const headline = rating ? ratingPilotHeadline(rating) : null;
  const evidence = rating ? ratingPilotEvidenceLabel(rating.evidenceState) : null;
  const interval = rating ? ratingPilotIntervalLabel(rating) : null;
  const snapshot = rating ? ratingPilotSnapshotLabel(rating.snapshotRecordedAt) : null;
  const through = rating ? ratingPilotThroughLabel(rating.throughPeriod) : null;
  const surfaceState = ratingPilotSurfaceState({
    loading: ratingQuery.isLoading,
    rating,
    error: ratingQuery.error,
    offline: !online,
  });
  const hasPublishedRating = surfaceState === 'ready'
    || surfaceState === 'stale_offline'
    || surfaceState === 'stale_failed';

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  useEffect(() => {
    const firstContext = contextsQuery.data?.[0];
    if (firstContext && !contextsQuery.data?.some(context => context.squadId === selectedSquadId)) {
      setSelectedSquadId(firstContext.squadId);
    }
  }, [contextsQuery.data, selectedSquadId]);

  if (!PILOT_UI_ENABLED) {
    return <Navigate to="/play" replace />;
  }

  return (
    <div className="max-w-3xl space-y-6 pb-24 md:pb-8">
      <header className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={() => navigate('/play')}
          className="flex h-11 w-11 items-center justify-center rounded-full text-text-subtle-readable hover:bg-surface-elevated hover:text-text"
          aria-label="Back to Matches"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <div>
          <p className="text-sm text-text-subtle-readable">Matches</p>
          <h1 className="text-2xl font-bold text-text">TARK Rating</h1>
        </div>
        {selectedContext ? (
          <button
            type="button"
            onClick={() => void Promise.allSettled([ratingQuery.refetch(), historyQuery.refetch(), ratedMatchesQuery.refetch()])}
            className="ml-auto flex h-11 w-11 items-center justify-center rounded-full text-text-subtle-readable hover:bg-surface-elevated hover:text-text disabled:opacity-50"
            aria-label="Refresh TARK rating"
            title="Refresh rating"
            disabled={ratingQuery.isFetching || historyQuery.isFetching || ratedMatchesQuery.isFetching}
            data-testid="rating-pilot-refresh"
          >
            <RefreshCw className={`h-5 w-5 ${ratingQuery.isFetching || historyQuery.isFetching || ratedMatchesQuery.isFetching ? 'motion-safe:animate-spin' : ''}`} aria-hidden="true" />
          </button>
        ) : null}
      </header>

      {(contextsQuery.data?.length ?? 0) > 1 ? (
        <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Rating squad">
          {contextsQuery.data?.map(context => (
            <button
              key={context.squadId}
              type="button"
              onClick={() => setSelectedSquadId(context.squadId)}
              aria-pressed={context.squadId === selectedContext?.squadId}
              className={`min-h-10 shrink-0 rounded-md border px-4 text-sm font-semibold ${context.squadId === selectedContext?.squadId ? 'border-primary bg-primary text-white' : 'border-border bg-surface text-text-secondary hover:border-primary/40'}`}
            >
              {context.squadName}
            </button>
          ))}
        </div>
      ) : null}

      {contextsQuery.isLoading ? (
        <RatingStatePanel state="loading" onRetry={() => void contextsQuery.refetch()} />
      ) : contextsQuery.error ? (
        <RatingStatePanel state={!online ? 'offline' : 'failed'} onRetry={() => void contextsQuery.refetch()} />
      ) : contextsQuery.data?.length === 0 ? (
        <div className="flex min-h-52 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border px-5 text-center" role="status" data-testid="rating-pilot-no-squads">
          <Gauge className="h-8 w-8 text-text-subtle-readable" aria-hidden="true" />
          <p className="font-semibold text-text">Rating not available yet</p>
          <p className="max-w-sm text-sm text-text-subtle-readable">Join an eligible squad and record eligible matches to build your TARK Rating.</p>
        </div>
      ) : (
        <>
          {hasPublishedRating && rating && headline && evidence ? (
            <>
              <RatingPilotHero headline={headline} evidenceLabel={evidence} interval={interval} />

              {surfaceState === 'stale_offline' || surfaceState === 'stale_failed' ? (
                <div
                  className="flex items-center gap-3 rounded-lg border border-border bg-surface-elevated px-4 py-3"
                  role="alert"
                  aria-live="polite"
                  data-testid={`rating-pilot-${surfaceState}`}
                >
                  {surfaceState === 'stale_offline'
                    ? <WifiOff className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                    : <RefreshCw className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />}
                  <p className="text-sm font-semibold text-text-subtle-readable">
                    {surfaceState === 'stale_offline'
                      ? 'Showing your saved rating while offline.'
                      : "Showing your saved rating because the latest refresh didn't finish."}
                  </p>
                </div>
              ) : null}

              <RatingPilotScaleBand rating={rating} />

              {rating.restated ? (
            <div
              className="flex items-start gap-3 rounded-lg border border-border bg-surface-elevated px-4 py-3"
              role="status"
              aria-label="Rating history recalculated. Your matches and scores did not change."
              data-testid="rating-pilot-restated"
            >
              <RefreshCw className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <div>
                <p className="font-semibold text-text">Rating history recalculated</p>
                <p className="mt-1 text-sm text-text-subtle-readable">Your matches and scores did not change.</p>
              </div>
            </div>
              ) : null}

              <section className="grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-3" aria-label="Rating evidence">
                <Metric label="Official matches" value={rating.officialMatchCount} />
                <Metric label="Distinct opponents" value={rating.distinctOpponentCount} />
                <EvidenceDate label="Through" value={through} />
              </section>

              <dl className="divide-y divide-border border-y border-border">
                <Detail label="Snapshot" value={snapshot} />
              </dl>

              <RatingPilotJourney
                history={historyQuery.data}
                loading={historyQuery.isLoading}
                error={Boolean(historyQuery.error)}
                onRetry={() => void historyQuery.refetch()}
                afterJourney={(
                  <RatingPilotRatedMatches
                    feed={ratedMatchesQuery.data}
                    loading={ratedMatchesQuery.isLoading}
                    error={Boolean(ratedMatchesQuery.error)}
                    onRetry={() => void ratedMatchesQuery.refetch()}
                  />
                )}
              />
            </>
          ) : surfaceState === 'loading' || surfaceState === 'unavailable' ? (
            <div className="flex min-h-44 flex-col items-center justify-center gap-2 px-5 text-center" role="status" data-testid="rating-pilot-building">
              {ratingQuery.isLoading ? <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" /> : <Gauge className="h-8 w-8 text-primary" aria-hidden="true" />}
              <p className="font-semibold text-text">Building your rating</p>
              <p className="max-w-sm text-sm leading-6 text-text-subtle-readable">Your rating will appear here after enough eligible match evidence is published.</p>
            </div>
          ) : (
            <RatingStatePanel state={surfaceState} onRetry={() => void ratingQuery.refetch()} />
          )}

          <p className="text-sm font-medium leading-6 text-text-subtle-readable">
            Eligible TARK match results build your rating. Methodology corrections may recalculate
            rating history; matches and scores stay unchanged.
          </p>
        </>
      )}
    </div>
  );
}

function RatingStatePanel({ state, onRetry }: { state: RatingPilotSurfaceState; onRetry: () => void }) {
  const copy = {
    loading: {
      icon: Loader2,
      title: 'Loading your rating',
      body: 'Checking your latest published snapshot.',
      retry: false,
    },
    offline: {
      icon: WifiOff,
      title: "You're offline",
      body: 'Reconnect to load your rating.',
      retry: true,
    },
    unavailable: {
      icon: Gauge,
      title: 'Rating not available yet',
      body: 'There is no published rating for this account.',
      retry: false,
    },
    paused: {
      icon: PauseCircle,
      title: 'Rating updates paused',
      body: 'Your matches and scores are preserved.',
      retry: false,
    },
    failed: {
      icon: AlertCircle,
      title: "Couldn't load your rating",
      body: 'Please try again.',
      retry: true,
    },
    stale_offline: {
      icon: WifiOff,
      title: 'Showing saved rating',
      body: 'Reconnect to refresh it.',
      retry: true,
    },
    stale_failed: {
      icon: RefreshCw,
      title: 'Showing saved rating',
      body: 'The latest refresh did not finish.',
      retry: true,
    },
    ready: {
      icon: Gauge,
      title: 'Rating ready',
      body: '',
      retry: false,
    },
  }[state];
  const Icon = copy.icon;

  return (
    <div
      className="flex min-h-52 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border px-5 text-center"
      role={state === 'failed' || state === 'offline' ? 'alert' : 'status'}
      aria-live="polite"
      data-testid={`rating-pilot-state-${state}`}
    >
      <Icon
        className={`h-8 w-8 text-text-subtle-readable ${state === 'loading' ? 'motion-safe:animate-spin' : ''}`}
        aria-hidden="true"
      />
      <p className="font-semibold text-text">{copy.title}</p>
      <p className="max-w-sm text-sm text-text-subtle-readable">{copy.body}</p>
      {copy.retry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 min-h-11 rounded-md bg-primary px-5 text-sm font-semibold text-white hover:bg-primary-dark"
          aria-label="Try loading the rating again"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | null }) {
  if (value == null) return null;
  return (
    <div className="bg-surface px-5 py-4">
      <p className="text-xl font-bold tabular-nums text-text">{value}</p>
      <p className="text-xs font-semibold text-text-subtle-readable">{label}</p>
    </div>
  );
}

function EvidenceDate({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="bg-surface px-5 py-4">
      <p className="text-sm font-bold tabular-nums text-text">{value}</p>
      <p className="text-xs font-semibold text-text-subtle-readable">{label}</p>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="grid min-w-0 grid-cols-1 gap-1 py-3 text-sm sm:grid-cols-[minmax(0,6rem)_minmax(0,1fr)] sm:gap-4">
      <dt className="font-semibold text-text-subtle-readable">{label}</dt>
      <dd className="min-w-0 break-words font-semibold text-text">{value}</dd>
    </div>
  );
}
